import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/client/lib/api-client'

// Types
export interface LicenseActivation {
  site_url: string
  plugin: string
  activated_at: string
}

export interface License {
  key: string
  status: 'active' | 'expired' | 'revoked'
  customer_email: string
  customer_name?: string
  plugins: string[]
  activations_limit: number
  activations: LicenseActivation[]
  created_at: string
  expires_at?: string
  notes?: string
}

export interface CreateLicenseData {
  customer_email: string
  customer_name?: string
  plugins?: string[]
  activations_limit?: number
  expires_at?: string
  notes?: string
}

export interface UpdateLicenseData {
  status?: 'active' | 'expired' | 'revoked'
  plugins?: string[]
  activations_limit?: number
  expires_at?: string
  notes?: string
}

// Query keys
export const licenseKeys = {
  all: ['licenses'] as const,
  lists: () => [...licenseKeys.all, 'list'] as const,
  list: () => [...licenseKeys.lists()] as const,
  details: () => [...licenseKeys.all, 'detail'] as const,
  detail: (key: string) => [...licenseKeys.details(), key] as const,
}

// Hooks
export function useLicenses() {
  return useQuery({
    queryKey: licenseKeys.list(),
    queryFn: async () => {
      const response = await apiClient.get<{ licenses: License[] }>('/api/update-server/licenses')
      return response.licenses || []
    },
  })
}

export function useLicense(key: string) {
  return useQuery({
    queryKey: licenseKeys.detail(key),
    queryFn: async () => {
      return apiClient.get<License>(`/api/update-server/licenses/${key}`)
    },
    enabled: !!key,
  })
}

export function useCreateLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateLicenseData) => {
      return apiClient.post<License>('/api/update-server/licenses', data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() })
    },
  })
}

export function useUpdateLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ key, data }: { key: string; data: UpdateLicenseData }) => {
      return apiClient.patch<License>(`/api/update-server/licenses/${key}`, data)
    },
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() })
      queryClient.invalidateQueries({ queryKey: licenseKeys.detail(key) })
    },
  })
}

export function useRevokeLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (key: string) => {
      return apiClient.delete(`/api/update-server/licenses/${key}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() })
    },
  })
}

export function useDeleteLicense() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (key: string) => {
      return apiClient.delete(`/api/update-server/licenses/${key}?permanent=true`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: licenseKeys.lists() })
    },
  })
}
